import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth, estAdmin } from '../lib/auth'
import type { Chantier, Profil } from '../lib/types'
import { ContactsChantier } from '../components/ContactsChantier'
import { ImportProcore } from '../components/ImportProcore'

export function Info({ chantier, recharger }: { chantier: Chantier; recharger: () => void }) {
  const { profil } = useAuth()
  const [membres, setMembres] = useState<Profil[]>([])
  const [tous, setTous] = useState<Profil[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const admin = estAdmin(profil)
  const [edition, setEdition] = useState(false)
  const [form, setForm] = useState(() => valeursDe(chantier))
  useEffect(() => setForm(valeursDe(chantier)), [chantier])

  async function enregistrerInfos(e: React.FormEvent) {
    e.preventDefault()
    if (!form.numero.trim() || !form.nom.trim()) {
      setErreur('Le numéro et le nom du chantier sont obligatoires.')
      return
    }
    setOccupe(true)
    setErreur(null)
    const vide = (v: string) => v.trim() || null
    const { error } = await supabase
      .from('chantiers')
      .update({
        numero: form.numero.trim(),
        nom: form.nom.trim(),
        adresse: vide(form.adresse),
        client: vide(form.client),
        entrepreneur_general: vide(form.eg)
      })
      .eq('id', chantier.id)
    setOccupe(false)
    if (error) {
      setErreur(
        error.code === '23505'
          ? `Le numéro « ${form.numero.trim()} » est déjà utilisé par un autre chantier.`
          : error.message
      )
      return
    }
    setEdition(false)
    recharger()
  }

  const charger = useCallback(async () => {
    const { data, error } = await supabase
      .from('chantier_membres')
      .select('profil_id, profils!chantier_membres_profil_id_fkey(id, nom, courriel, role, actif)')
      .eq('chantier_id', chantier.id)
    if (error) {
      setErreur(error.message)
      return
    }
    const liste = (data ?? [])
      .map((m) => (m as unknown as { profils: Profil | null }).profils)
      .filter((p): p is Profil => Boolean(p))
    liste.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
    setMembres(liste)

    if (admin) {
      const { data: t } = await supabase
        .from('profils')
        .select('id, nom, courriel, role, actif')
        .eq('actif', true)
        .order('nom')
      setTous((t as Profil[]) ?? [])
    }
  }, [chantier.id, admin])

  useEffect(() => {
    void charger()
  }, [charger])

  async function assigner(profilId: string) {
    setOccupe(true)
    const { error } = await supabase
      .from('chantier_membres')
      .insert({ chantier_id: chantier.id, profil_id: profilId, assigne_par: profil?.id ?? null })
    if (error) setErreur(error.message)
    await charger()
    setOccupe(false)
  }

  async function retirer(profilId: string) {
    setOccupe(true)
    const { error } = await supabase
      .from('chantier_membres')
      .delete()
      .eq('chantier_id', chantier.id)
      .eq('profil_id', profilId)
    if (error) setErreur(error.message)
    await charger()
    setOccupe(false)
  }

  async function changerStatut(statut: Chantier['statut']) {
    setOccupe(true)
    const { error } = await supabase.from('chantiers').update({ statut }).eq('id', chantier.id)
    if (error) setErreur(error.message)
    else recharger()
    setOccupe(false)
  }

  const disponibles = tous.filter((p) => !membres.some((m) => m.id === p.id))

  return (
    <>
      {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}

      <div className="sect">
        <span>Chantier</span>
        {admin && chantier.statut !== 'termine' && !edition && (
          <button className="retour" onClick={() => setEdition(true)}>
            Modifier
          </button>
        )}
      </div>
      {edition ? (
        <form className="carte bande-nulle" onSubmit={(e) => void enregistrerInfos(e)}>
          <Champ etiquette="Numéro *" valeur={form.numero} changer={(v) => setForm({ ...form, numero: v })} />
          <Champ etiquette="Nom du chantier *" valeur={form.nom} changer={(v) => setForm({ ...form, nom: v })} />
          <Champ etiquette="Adresse" valeur={form.adresse} changer={(v) => setForm({ ...form, adresse: v })} />
          <Champ etiquette="Client" valeur={form.client} changer={(v) => setForm({ ...form, client: v })} />
          <Champ etiquette="Entrepreneur général" valeur={form.eg} changer={(v) => setForm({ ...form, eg: v })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="bouton creux"
              disabled={occupe}
              onClick={() => {
                setForm(valeursDe(chantier))
                setEdition(false)
                setErreur(null)
              }}
            >
              Annuler
            </button>
            <button className="bouton" disabled={occupe}>
              {occupe ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      ) : (
      <div className="carte bande-nulle">
        <Ligne etiquette="Numéro" valeur={chantier.numero} />
        <Ligne etiquette="Nom" valeur={chantier.nom} />
        <Ligne etiquette="Client" valeur={chantier.client} />
        <Ligne etiquette="Ent. général" valeur={chantier.entrepreneur_general} />
        <Ligne etiquette="Adresse" valeur={chantier.adresse} />
        <Ligne
          etiquette="Statut"
          valeur={
            chantier.statut === 'termine'
              ? 'Archivé — lecture seule'
              : chantier.statut === 'suspendu'
                ? 'Suspendu'
                : 'Actif'
          }
        />
      </div>
      )}

      <ContactsChantier chantierId={chantier.id} modifiable={admin && chantier.statut !== 'termine'} />

      <div className="sect">
        Équipe assignée <span className="code">{membres.length}</span>
      </div>
      <div className="liste">
        {membres.map((m) => (
          <div key={m.id} className="carte bande-nulle">
            <div className="entre">
              <div>
                <p className="titre">{m.nom}</p>
                <p className="meta">
                  <span>{libelleRole(m.role)}</span>
                </p>
              </div>
              {admin && chantier.statut !== 'termine' && (
                <button
                  className="pastille p-ouvert"
                  onClick={() => void retirer(m.id)}
                  disabled={occupe}
                >
                  Retirer
                </button>
              )}
            </div>
          </div>
        ))}
        {membres.length === 0 && <p className="vide">Personne n'est assigné à ce chantier.</p>}
      </div>

      {admin && chantier.statut !== 'termine' && disponibles.length > 0 && (
        <>
          <div className="sect">Ajouter au chantier</div>
          <div className="liste">
            {disponibles.map((p) => (
              <button
                key={p.id}
                className="carte bande-nulle"
                onClick={() => void assigner(p.id)}
                disabled={occupe}
              >
                <div className="entre">
                  <div>
                    <p className="titre">{p.nom}</p>
                    <p className="meta">
                      <span>{libelleRole(p.role)}</span>
                      <span className="pt" />
                      <span>{p.courriel}</span>
                    </p>
                  </div>
                  <span className="pastille p-alerte">Ajouter</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {admin && (
        <>
          <div className="sect">Administration</div>
          {chantier.statut !== 'termine' && <ImportProcore chantierId={chantier.id} />}
          <div style={{ height: 16 }} />
          {chantier.statut !== 'termine' ? (
            <button
              className="bouton creux"
              disabled={occupe}
              onClick={() => {
                if (
                  window.confirm(
                    'Archiver ce chantier ? Il deviendra consultable en lecture seule : ' +
                      'plus aucune photo, note ou modification ne sera possible.'
                  )
                ) {
                  void changerStatut('termine')
                }
              }}
            >
              Archiver le chantier
            </button>
          ) : (
            <button className="bouton creux" disabled={occupe} onClick={() => void changerStatut('actif')}>
              Réactiver le chantier
            </button>
          )}
        </>
      )}
    </>
  )
}

function Ligne({ etiquette, valeur }: { etiquette: string; valeur: string | null | undefined }) {
  return (
    <p className="meta" style={{ marginTop: 6, gap: 8 }}>
      <span style={{ minWidth: 96, fontWeight: 700 }}>{etiquette}</span>
      <span style={{ color: 'var(--ink)' }}>{valeur || '—'}</span>
    </p>
  )
}

function libelleRole(r: string): string {
  if (r === 'superadmin') return 'Superadmin'
  if (r === 'admin') return 'Administrateur'
  if (r === 'chef') return "Chef d'équipe"
  return 'Consultation'
}

function valeursDe(c: Chantier) {
  return {
    numero: c.numero ?? '',
    nom: c.nom ?? '',
    adresse: c.adresse ?? '',
    client: c.client ?? '',
    eg: c.entrepreneur_general ?? ''
  }
}

function Champ({
  etiquette,
  valeur,
  changer
}: {
  etiquette: string
  valeur: string
  changer: (v: string) => void
}) {
  return (
    <label className="champ">
      <span>{etiquette}</span>
      <input value={valeur} onChange={(e) => changer(e.target.value)} />
    </label>
  )
}
